import { useEffect, type ReactNode } from 'react';
import { NavBar } from '../NavBar';
import { TopBar } from '../TopBar';
import { StatusBar } from '../StatusBar';
import { LayoutNotificationHost } from '../LayoutNotificationHost';
import { SessionIdleGuard } from '../shared/SessionWatch';
import { MemoPanel } from '../MemoPanel';
import { AiPanel } from '../AiPanel';
import { RightRailHost } from '../RightRailHost';
import { SettingsModalHost } from '../SettingsModal';
import { SignatureHost } from '../SignatureHost';
// 사람 프로필(2026-08-31 · 시안 8/9) — 아바타를 누르는 자리는 여럿인데 **팝업은 한 벌**이다.
import { PersonProfileHost } from '../PersonProfileHost';
// 화면잠금(2026-08-19) — 자리를 비우면 화면을 덮는 가림막. 전역 소유(진입점은 세션 엔진·단축키).
import { ScreenLockHost } from '../ScreenLock';
import { ToastViewport } from '../shared/Toast';
import { ErrorBoundary } from '../shared/ErrorBoundary';
import { useSidebarStore } from '../../store/sidebarStore';
import { SHELL_STATUSBAR_ENABLED, SHELL_FULLHEIGHT_RAIL_ENABLED } from '../../lib/layout-shell';
import { installCustomFonts } from '../../lib/custom-fonts';
import './AppLayout.css';
/* 풀하이트 레일 보정 — 상태바가 본문 칸으로 들어가면서 어긋나는 것만 되돌린다(기존 CSS 무수정). */
import './AppLayoutFullHeight.css';

export function AppLayout({ children }: { children: ReactNode }) {
  // 접힘 상태는 부모 data-collapsed 로만 노출 — NavBar.tsx 무수정(CSS 가 접힘 표현 담당, 회귀 방지).
  //
  // 2026-08-11 — 좌측 경계의 접기/펼치기 **버튼(SidebarToggle) 삭제**(사용자 지시).
  //   접힘 자체는 남는다.
  //
  // 2026-09-07 — 진입점이 **레일 머리의 토글 버튼**(정본 H1-4)과 아래 `Ctrl/Cmd+B` 로 바뀌었다.
  //   개인 설정 모달의 "사이드바 아이콘만 표시" 스위치는 걷어냈다(사용자 지시) — 레일에 버튼이
  //   생긴 뒤로 그 자리는 «찾아 들어가야 하는 두 번째 길» 이었다.
  //   함께 제거: `useAutoCollapseSidebar`(폭 ≤1024px 자동 아이콘화). 이유 두 가지 —
  //     ① 그 훅은 "좁은 폭에서도 SidebarToggle 로 수동 복귀 가능"을 전제로 만들어졌는데 버튼이 없어짐
  //     ② 레일이 세로 배치로 이미 72px 이라 접어도 64px, 8px 밖에 못 번다(자동 개입할 값이 없음)
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggleSidebar = useSidebarStore((s) => s.toggle);

  /**
   * `Ctrl/Cmd + B` — 좌측 레일 펼치기·접기 (정본 H1-4 · 단축키 목록은 H4).
   *
   * ⚠ **브라우저가 먼저 가져간다** — 파이어폭스는 `Ctrl+B` 가 북마크 사이드바다. 그래서
   *   `preventDefault()` 로 막는다.
   * ⚠ **입력 중에는 잡지 않는다**(`input`·`textarea`·`contenteditable`) — 안 그러면 글을
   *   쓰다가 레일이 접힌다(정본 H4 의 규칙).
   * 종전에는 `NavBar.css` 주석에만 「Ctrl/Cmd+B」라고 적혀 있고 **핸들러가 없었다** —
   * 눌러도 아무 일이 없었다.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      if (e.key !== 'b' && e.key !== 'B') return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      e.preventDefault();
      toggleSidebar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleSidebar]);

  /*
    회사 글꼴 (2026-08-18) — 관리자가 등록한 `@font-face` 를 한 번 붙인다.
    로그인한 화면 전체가 이 셸 아래에 있으므로 여기가 "한 번" 이 성립하는 자리다.
    실패는 조용히 넘어간다(글꼴은 덤 — 없으면 지금까지처럼 대체 글꼴로 그린다).
  */
  useEffect(() => { void installCustomFonts(); }, []);
  // ⑥ 상태바를 **본문 칸 안**에 놓는가 (풀하이트 레일 — 시안 mockup-20260817-fullheight-rail-default).
  //   ON  : `.app-main` 의 마지막 자식 → 좌·우 레일이 상하를 관통하고 상태바는 본문 폭만 차지한다.
  //   OFF : 종전 자리(`.app-shell` 직속, 레일 아래까지 전폭).
  // 플래그 OFF 면 아래 두 분기가 예전 코드와 완전히 같은 트리를 만든다(회귀 0).
  const statusBarInline = SHELL_STATUSBAR_ENABLED && SHELL_FULLHEIGHT_RAIL_ENABLED;
  // 공통 레이아웃 골격(design-20260717) — 외곽 column 래퍼로 ⑥ 하단 상태바를 담을 수 있게 한다.
  // StatusBar 는 flag OFF 기본(Phase 0) — 미렌더 시 .app-layout(flex:1) 이 전체 높이를 차지해 시각 무변화.
  return (
    <div className="app-shell" data-statusbar-inline={statusBarInline || undefined}>
      {/* 알림/미읽음/SSE 단일 호스트 — 항상 mount(TopBar/StatusBar 표시와 독립). design-20260717 Phase 2-A. */}
      <LayoutNotificationHost />
      {/* 60분 idle 세션 만료·자동 로그아웃 — 화면과 무관하게 상시 구독(headless).
          세션 시간 표시는 하단 상태바(StatusBar 맨 오른쪽)가 담당한다(2026-08-11 이관).
          표시가 없는 화면에서도 만료 처리가 끊기지 않도록 여기서 엔진을 붙든다. */}
      <SessionIdleGuard />
      {/* ⑤ 우측 레일 메모 — 전역 소유(design-20260724 §4.3).
          레일은 캘린더만 opt-in 이라 레일 버튼만 배선하면 그 화면에서만 열린다 →
          패널 자체는 여기 하나만 두고, 진입점(레일·단축키·상태바)이 store 를 토글한다. */}
      <MemoPanel />
      {/* 우측 AI 패널 — 전역 소유(설계 판 A). 진입점(대시보드의 떠 있는 단추 · ⑤ 레일의 AI 칸)은
          aiPanelStore 만 건드리고, 본체는 여기 하나만 둔다(화면마다 복제 금지 — 메모와 같은 계약). */}
      <AiPanel />
      {/* 설정 모달(2026-08-12) — 전역 소유. 진입점(⑤ 레일 프로필 팝오버 · 대시보드 위젯)은
          profileUiStore 만 건드리고, 본체는 여기 하나만 둔다(화면마다 복제 금지). */}
      <SettingsModalHost />
      {/* 내 서명 — 프로필 팝오버가 닫혀도 살아 있어야 해서 셸이 들고 있다(판 8 잔여 ②). */}
      <SignatureHost />
      {/* 사람 프로필 — 여는 자리(메시지 아바타 · 목록 행)는 스크롤되며 사라질 수 있어 셸이 든다.
          여는 쪽은 `openPersonProfile(userId)` 하나만 부르고, 무엇이 보이나는 서버가 정한다. */}
      <PersonProfileHost />
      {/* 화면잠금 — 잠기지 않았으면 아무것도 그리지 않는다. 잠기면 화면 전체를 덮는다.
          세션 엔진(lib/session-idle)의 만료를 가로채 로그아웃 대신 잠금으로 바꾼다. */}
      <ScreenLockHost />
      <div className="app-layout" data-collapsed={collapsed || undefined}>
        <NavBar />
        <div className="app-main">
          {/* design-20260717 Phase 2 — 상단 TopBar 는 하단 StatusBar 로 전면 대체(flag ON). flag OFF 시 기존 TopBar 유지. */}
          {!SHELL_STATUSBAR_ENABLED && <TopBar />}
          <main className="app-content scrollbar-thin">
            <ErrorBoundary>{children}</ErrorBoundary>
          </main>
          {/* ⑥ 하단 상태바 — 풀하이트 레일에서는 여기(본문 칸)에 놓인다.
              `.app-content` 는 flex:1, 상태바는 flex:none(30px) 이라 본문 높이는 종전과 같다
              (예전에도 `.app-shell` 이 100vh 를 상태바와 나눠 갖고 있었다). */}
          {statusBarInline && <StatusBar />}
        </div>
        {/* ⑤ 우측 사이드 레일 — 전역 공통 요소(all-frame.html 6a). 종전 캘린더 opt-in 에서 승격.
            표시 여부는 layoutShellStore 가 소유하며 라우트 전환에도 유지된다. */}
        <RightRailHost />
        <ToastViewport />
      </div>
      {/* 종전 자리 — 풀하이트 레일 OFF(롤백) 에서만 여기에 렌더된다. */}
      {SHELL_STATUSBAR_ENABLED && !statusBarInline && <StatusBar />}
    </div>
  );
}
